import { useMemo } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import type { RevenueChartResponseDto } from '@/generated/korza-api-client/types.gen';
import { type ChartGranularity } from '@/lib/chart-axis';
import { bucketMergeChartData } from '@/lib/hooks/use-chart-data';
import { usePartialBuckets } from '@/lib/hooks/use-partial-buckets';
import { markPartialRows, type ChartRange } from '@/lib/partial-buckets';
import { useChartTooltip } from '@/lib/hooks/use-chart-tooltip';
import { MetricsAreaChart } from '@/components/ui/charts/area-chart';
import {
  MetricsChartAreaSeries,
  MetricsChartLineSeries,
} from '@/components/ui/charts/chart-area-series';
import { PREVIOUS_PERIOD_DASH } from '@/components/ui/charts/chart-legend-items';
import { alignByIndex } from '@/lib/period-comparison';
import { ChartClickHint } from '@/components/ui/charts/chart-click-hint';

export interface SalesDataPoint {
  count: number;
  grossRevenueUsd: number;
}

type SalesChartPointDto = RevenueChartResponseDto['points'][number];

export type Granularity = ChartGranularity;

interface SalesChartProps {
  granularity: ChartGranularity;
  currency: string;
  points: SalesChartPointDto[];
  /**
   * The previous period's points (ANLT-04): a gray dashed line aligned by
   * bucket index.
   */
  previousPoints?: SalesChartPointDto[];
  /** The filter's period: marks the partial first/last bucket (CHART-01). */
  range?: ChartRange;
  syncId?: string;
  syncMethod?: 'index' | 'value';
  /**
   * When provided, each period is clickable; `bucketStart` is the point's
   * original api date.
   */
  onBucketClick?: (bucketStart: string) => void;
}

const PREVIOUS_LINE_COLOR = 'hsl(var(--muted-foreground))';

/** Revenue per bucket (sorted, merged by bucket). */
function revenueRows(
  points: SalesChartPointDto[],
  granularity: ChartGranularity,
  locale: Locales
) {
  return bucketMergeChartData<SalesChartPointDto, SalesDataPoint>(
    points,
    granularity,
    locale,
    (point, cur) => {
      const base = cur ?? { count: 0, grossRevenueUsd: 0 };
      return {
        count: base.count + point.count,
        grossRevenueUsd: base.grossRevenueUsd + point.grossRevenueUsd,
      };
    },
    (_iso, record) => ({ revenue: record.grossRevenueUsd })
  ).map((row) => ({
    month: row.month,
    date: String(row.date),
    revenue: Number(row.revenue),
  }));
}

/**
 * Renders revenue from API `points` only (sorted, merged by bucket), with the
 * previous period as a gray dashed line when given.
 */
export function SalesChart({
  currency,
  granularity,
  points,
  previousPoints,
  range,
  syncId,
  syncMethod,
  onBucketClick,
}: SalesChartProps) {
  const { LL, locale } = useI18nContext();

  const rows = useMemo(
    () => revenueRows(points, granularity, locale),
    [granularity, locale, points]
  );
  const previousRevenue = useMemo(
    () =>
      previousPoints
        ? revenueRows(previousPoints, granularity, locale).map(
            (row) => row.revenue
          )
        : null,
    [granularity, locale, previousPoints]
  );
  const partial = usePartialBuckets(rows, range, granularity);
  const chartData = useMemo(() => {
    const marked = markPartialRows(rows, 'revenue', partial.edges);
    return previousRevenue ? alignByIndex(marked, previousRevenue) : marked;
  }, [rows, partial.edges, previousRevenue]);

  const handleBucketClick = onBucketClick
    ? (row: Record<string, unknown>) => {
        if (typeof row.date === 'string') onBucketClick(row.date);
      }
    : undefined;

  const tooltipContent = useChartTooltip({
    footer: onBucketClick ? <ChartClickHint /> : undefined,
    formatValue: (value) =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(value),
    formatRows: (label, payload) => [
      {
        label: LL.general.revenue(),
        value: new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
        }).format((payload[0]?.value as number) ?? 0),
      },
    ],
  });

  return (
    <MetricsAreaChart
      data={chartData}
      partialLegend={partial.edges}
      legendLines={
        previousRevenue
          ? [
              {
                label: LL.metricsPage.salesTab.previousPeriod(),
                color: PREVIOUS_LINE_COLOR,
                dash: PREVIOUS_PERIOD_DASH,
              },
            ]
          : undefined
      }
      syncId={syncId}
      syncMethod={syncMethod}
      tooltipContent={tooltipContent}
      onBucketClick={handleBucketClick}
    >
      <MetricsChartAreaSeries
        dataKey="revenue"
        strokeWidth={1}
        partialDataKey="revenuePartial"
        partialLabels={partial.labels}
      />
      {previousRevenue ? (
        <MetricsChartLineSeries
          dataKey="previous"
          name={LL.metricsPage.salesTab.previousPeriod()}
          color={PREVIOUS_LINE_COLOR}
          dash={PREVIOUS_PERIOD_DASH}
        />
      ) : null}
    </MetricsAreaChart>
  );
}
